(function () {
  'use strict';

  var root = document.getElementById('DrbConsentManager');
  if (!root) return;

  var STORAGE_KEY = 'drbConsentStateV1';
  var VISITOR_KEY = 'drbConsentVisitorIdV1';
  var CONSENT_CATEGORIES = ['preferences', 'analytics', 'marketing'];
  var lastFocusedElement = null;
  var customerPrivacy = null;

  var config = {
    shopDomain: root.dataset.shopDomain || '',
    storefrontHost: root.dataset.storefrontHost || window.location.host,
    locale: root.dataset.locale || document.documentElement.lang || '',
    country: root.dataset.country || '',
    policyVersion: root.dataset.policyVersion || '2026-05-29',
    policyUrl: root.dataset.policyUrl || '/policies/privacy-policy',
    supabaseEndpoint: root.dataset.supabaseEndpoint || '',
    ingestKey: root.dataset.ingestKey || '',
    respectShopifyRegion: root.dataset.respectShopifyRegion === 'true',
    showFloatingButton: root.dataset.showFloatingButton !== 'false',
  };

  var banner = root.querySelector('.drb-cmp__banner');
  var modal = root.querySelector('.drb-cmp__modal');
  var modalPanel = root.querySelector('.drb-cmp__modal-panel');
  var floatingButton = root.querySelector('.drb-cmp__floating');
  var categoryInputs = root.querySelectorAll('[data-cmp-category]');

  function storageGet(key) {
    try {
      return window.localStorage.getItem(key);
    } catch (error) {
      return null;
    }
  }

  function storageSet(key, value) {
    try {
      window.localStorage.setItem(key, value);
    } catch (error) {
      // Consent still applies through Shopify even if local storage is unavailable.
    }
  }

  function uuid() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return window.crypto.randomUUID();
    }

    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (character) {
      var random = (Math.random() * 16) | 0;
      var value = character === 'x' ? random : (random & 0x3) | 0x8;
      return value.toString(16);
    });
  }

  function getVisitorId() {
    var visitorId = storageGet(VISITOR_KEY);
    if (!visitorId) {
      visitorId = uuid();
      storageSet(VISITOR_KEY, visitorId);
    }
    return visitorId;
  }

  function readSavedState() {
    try {
      var saved = JSON.parse(storageGet(STORAGE_KEY) || 'null');
      if (!saved || saved.policyVersion !== config.policyVersion || !saved.consent) return null;
      return saved;
    } catch (error) {
      return null;
    }
  }

  function saveState(action, consent, metadata) {
    var state = {
      action: action,
      consent: normalizeConsent(consent),
      visitorId: getVisitorId(),
      policyVersion: config.policyVersion,
      policyUrl: config.policyUrl,
      timestamp: new Date().toISOString(),
      shopifyConsentId: metadata && metadata.shopifyConsentId ? metadata.shopifyConsentId : '',
    };

    storageSet(STORAGE_KEY, JSON.stringify(state));
    window.DrbConsentDefaults = window.DrbConsentDefaults || {};
    window.DrbConsentDefaults.consent = state.consent;
    return state;
  }

  function normalizeConsent(consent) {
    return {
      preferences: consent && consent.preferences === true,
      analytics: consent && consent.analytics === true,
      marketing: consent && consent.marketing === true,
    };
  }

  function allConsent(value) {
    return {
      preferences: value,
      analytics: value,
      marketing: value,
    };
  }

  function consentFromShopifyValue(value) {
    return value === 'yes' ? true : value === 'no' ? false : null;
  }

  function hasCompleteShopifyDecision(currentConsent) {
    if (!currentConsent) return false;
    return CONSENT_CATEGORIES.every(function (category) {
      return currentConsent[category] === 'yes' || currentConsent[category] === 'no';
    });
  }

  function consentFromShopify(currentConsent) {
    return {
      preferences: consentFromShopifyValue(currentConsent.preferences) === true,
      analytics: consentFromShopifyValue(currentConsent.analytics) === true,
      marketing: consentFromShopifyValue(currentConsent.marketing) === true,
    };
  }

  function loadCustomerPrivacy() {
    return new Promise(function (resolve) {
      if (window.Shopify && window.Shopify.customerPrivacy) {
        resolve(window.Shopify.customerPrivacy);
        return;
      }

      if (!window.Shopify || typeof window.Shopify.loadFeatures !== 'function') {
        resolve(null);
        return;
      }

      window.Shopify.loadFeatures(
        [
          {
            name: 'consent-tracking-api',
            version: '0.1',
          },
        ],
        function (error) {
          resolve(error ? null : window.Shopify.customerPrivacy || null);
        }
      );
    });
  }

  function getShopifySnapshot() {
    var snapshot = {
      currentVisitorConsent: null,
      consentId: '',
      region: '',
      allowed: {},
    };

    if (!customerPrivacy) return snapshot;

    try {
      snapshot.currentVisitorConsent = customerPrivacy.currentVisitorConsent
        ? customerPrivacy.currentVisitorConsent()
        : null;
    } catch (error) {
      snapshot.currentVisitorConsent = null;
    }

    try {
      snapshot.consentId = customerPrivacy.consentId ? customerPrivacy.consentId() : '';
    } catch (error) {
      snapshot.consentId = '';
    }

    try {
      snapshot.region = customerPrivacy.getRegion ? customerPrivacy.getRegion() : '';
    } catch (error) {
      snapshot.region = '';
    }

    try {
      snapshot.allowed = {
        preferences: customerPrivacy.preferencesProcessingAllowed
          ? customerPrivacy.preferencesProcessingAllowed()
          : false,
        analytics: customerPrivacy.analyticsProcessingAllowed ? customerPrivacy.analyticsProcessingAllowed() : false,
        marketing: customerPrivacy.marketingAllowed ? customerPrivacy.marketingAllowed() : false,
        saleOfData: customerPrivacy.saleOfDataAllowed ? customerPrivacy.saleOfDataAllowed() : false,
      };
    } catch (error) {
      snapshot.allowed = {};
    }

    return snapshot;
  }

  function setShopifyConsent(consent) {
    var normalized = normalizeConsent(consent);

    return new Promise(function (resolve) {
      if (!customerPrivacy || typeof customerPrivacy.setTrackingConsent !== 'function') {
        resolve();
        return;
      }

      customerPrivacy.setTrackingConsent(
        {
          preferences: normalized.preferences,
          analytics: normalized.analytics,
          marketing: normalized.marketing,
        },
        resolve
      );
    });
  }

  function updateGoogleConsent(consent) {
    var normalized = normalizeConsent(consent);
    var payload = {
      ad_storage: normalized.marketing ? 'granted' : 'denied',
      ad_user_data: normalized.marketing ? 'granted' : 'denied',
      ad_personalization: normalized.marketing ? 'granted' : 'denied',
      analytics_storage: normalized.analytics ? 'granted' : 'denied',
      functionality_storage: normalized.preferences ? 'granted' : 'denied',
      personalization_storage: normalized.preferences ? 'granted' : 'denied',
      security_storage: 'granted',
    };

    if (typeof window.gtag === 'function') {
      window.gtag('consent', 'update', payload);
    } else {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(['consent', 'update', payload]);
    }
  }

  function loadConsentedScripts(consent) {
    var normalized = normalizeConsent(consent);
    var scripts = document.querySelectorAll('script[type="text/plain"][data-consent-category]');

    scripts.forEach(function (placeholder) {
      var category = placeholder.dataset.consentCategory;
      if (!normalized[category] || placeholder.dataset.consentLoaded === 'true') return;

      var script = document.createElement('script');
      var dataType = placeholder.dataset.type || 'text/javascript';

      script.type = dataType;
      script.async = placeholder.dataset.async === 'true';
      script.defer = placeholder.dataset.defer === 'true';

      if (placeholder.nonce) {
        script.nonce = placeholder.nonce;
      }

      Array.prototype.forEach.call(placeholder.attributes, function (attribute) {
        if (
          attribute.name === 'type' ||
          attribute.name === 'data-src' ||
          attribute.name === 'data-type' ||
          attribute.name === 'data-consent-category' ||
          attribute.name === 'data-consent-loaded' ||
          attribute.name === 'data-async' ||
          attribute.name === 'data-defer'
        ) {
          return;
        }

        script.setAttribute(attribute.name, attribute.value);
      });

      if (placeholder.dataset.src) {
        script.src = placeholder.dataset.src;
      } else {
        script.text = placeholder.textContent || '';
      }

      placeholder.dataset.consentLoaded = 'true';
      placeholder.parentNode.insertBefore(script, placeholder.nextSibling);
    });
  }

  function applyConsent(consent) {
    var normalized = normalizeConsent(consent);
    updateGoogleConsent(normalized);
    loadConsentedScripts(normalized);
    document.dispatchEvent(
      new CustomEvent('drb:consent:changed', {
        detail: {
          consent: normalized,
          policyVersion: config.policyVersion,
        },
      })
    );
  }

  function setInputs(consent) {
    var normalized = normalizeConsent(consent);
    categoryInputs.forEach(function (input) {
      input.checked = normalized[input.dataset.cmpCategory] === true;
    });
  }

  function getInputsConsent() {
    var consent = {};
    categoryInputs.forEach(function (input) {
      consent[input.dataset.cmpCategory] = input.checked === true;
    });
    return normalizeConsent(consent);
  }

  function showRoot() {
    root.hidden = false;
  }

  function showBanner() {
    showRoot();
    if (banner) banner.hidden = false;
    if (floatingButton) floatingButton.hidden = true;
  }

  function hideBanner() {
    if (banner) banner.hidden = true;
  }

  function showFloatingButton() {
    if (!config.showFloatingButton || !floatingButton) return;
    showRoot();
    floatingButton.hidden = false;
  }

  function openModal() {
    var saved = readSavedState();
    lastFocusedElement = document.activeElement;
    showRoot();
    hideBanner();
    setInputs(saved ? saved.consent : allConsent(false));
    modal.hidden = false;
    document.body.classList.add('drb-cmp-modal-open');
    window.setTimeout(function () {
      modalPanel.focus();
    }, 0);
  }

  function closeModal() {
    modal.hidden = true;
    document.body.classList.remove('drb-cmp-modal-open');
    showFloatingButton();

    if (lastFocusedElement && typeof lastFocusedElement.focus === 'function') {
      lastFocusedElement.focus();
    }
  }

  function setButtonsDisabled(isDisabled) {
    root.querySelectorAll('button[data-cmp-action]').forEach(function (button) {
      button.disabled = isDisabled;
    });
  }

  function getActionName(requestedAction, consent, previousState) {
    var normalized = normalizeConsent(consent);
    var hadPriorAcceptedCategory =
      previousState &&
      previousState.consent &&
      CONSENT_CATEGORIES.some(function (category) {
        return previousState.consent[category] === true;
      });
    var hasCurrentAcceptedCategory = CONSENT_CATEGORIES.some(function (category) {
      return normalized[category] === true;
    });

    if (hadPriorAcceptedCategory && !hasCurrentAcceptedCategory) {
      return 'withdraw';
    }

    return requestedAction;
  }

  function buildProofPayload(action, consent, previousState, savedState, snapshot) {
    return {
      event_id: uuid(),
      source: 'shopify-theme-cmp',
      action: action,
      visitor_id: savedState.visitorId,
      shop_domain: config.shopDomain,
      storefront_host: config.storefrontHost,
      locale: config.locale,
      country: config.country,
      policy_version: config.policyVersion,
      policy_url: config.policyUrl,
      page_url: window.location.href,
      referrer: document.referrer || '',
      user_agent: window.navigator.userAgent,
      timestamp: savedState.timestamp,
      consent: normalizeConsent(consent),
      previous_consent: previousState ? normalizeConsent(previousState.consent) : null,
      shopify_consent_id: snapshot.consentId || savedState.shopifyConsentId || '',
      shopify_region: snapshot.region || '',
      shopify_current_visitor_consent: snapshot.currentVisitorConsent,
      shopify_allowed: snapshot.allowed,
      proof: {
        ui: 'custom-cmp-banner-v1',
        buttons_presented: ['accept_all', 'reject_all', 'customize'],
        categories_presented: ['required', 'preferences', 'analytics', 'marketing'],
        required_cookies_locked: true,
      },
    };
  }

  function logConsent(payload) {
    if (!config.supabaseEndpoint) {
      return Promise.resolve({ skipped: true });
    }

    var headers = {
      'Content-Type': 'application/json',
    };

    if (config.ingestKey) {
      headers['X-CMP-Ingest-Key'] = config.ingestKey;
    }

    return fetch(config.supabaseEndpoint, {
      method: 'POST',
      mode: 'cors',
      credentials: 'omit',
      keepalive: true,
      headers: headers,
      body: JSON.stringify(payload),
    }).catch(function (error) {
      if (window.console && window.console.warn) {
        window.console.warn('Consent audit log failed', error);
      }
    });
  }

  function handleConsent(consent, requestedAction) {
    var previousState = readSavedState();
    var action = getActionName(requestedAction, consent, previousState);

    setButtonsDisabled(true);

    setShopifyConsent(consent)
      .then(function () {
        var snapshot = getShopifySnapshot();
        var savedState = saveState(action, consent, {
          shopifyConsentId: snapshot.consentId,
        });
        var payload = buildProofPayload(action, consent, previousState, savedState, snapshot);

        applyConsent(consent);
        hideBanner();
        closeModal();
        showFloatingButton();
        return logConsent(payload);
      })
      .finally(function () {
        setButtonsDisabled(false);
      });
  }

  function shouldShowForCurrentRegion() {
    if (!config.respectShopifyRegion || !customerPrivacy || typeof customerPrivacy.shouldShowBanner !== 'function') {
      return true;
    }

    try {
      return customerPrivacy.shouldShowBanner() === true;
    } catch (error) {
      return true;
    }
  }

  function initFromShopifyOrSavedState() {
    var saved = readSavedState();
    if (saved) {
      applyConsent(saved.consent);
      hideBanner();
      showFloatingButton();
      return;
    }

    var snapshot = getShopifySnapshot();
    if (hasCompleteShopifyDecision(snapshot.currentVisitorConsent)) {
      var consent = consentFromShopify(snapshot.currentVisitorConsent);
      saveState('shopify_existing', consent, {
        shopifyConsentId: snapshot.consentId,
      });
      applyConsent(consent);
      showFloatingButton();
      return;
    }

    if (!shouldShowForCurrentRegion()) {
      applyConsent({
        preferences: snapshot.allowed.preferences === true,
        analytics: snapshot.allowed.analytics === true,
        marketing: snapshot.allowed.marketing === true,
      });
      showFloatingButton();
      return;
    }

    showBanner();
  }

  function handleClick(event) {
    var button = event.target.closest('[data-cmp-action]');
    if (!button || !root.contains(button)) return;

    var action = button.dataset.cmpAction;

    if (action === 'accept-all') {
      handleConsent(allConsent(true), 'accept_all');
    }

    if (action === 'reject-all') {
      handleConsent(allConsent(false), 'reject_all');
    }

    if (action === 'customize' || action === 'open-settings') {
      openModal();
    }

    if (action === 'save-choices') {
      handleConsent(getInputsConsent(), 'customize');
    }

    if (action === 'close-modal') {
      if (readSavedState()) {
        closeModal();
      } else {
        modal.hidden = true;
        document.body.classList.remove('drb-cmp-modal-open');
        showBanner();
      }
    }
  }

  function handleKeydown(event) {
    if (event.key !== 'Escape' || modal.hidden) return;

    if (readSavedState()) {
      closeModal();
    } else {
      modal.hidden = true;
      document.body.classList.remove('drb-cmp-modal-open');
      showBanner();
    }
  }

  root.addEventListener('click', handleClick);
  document.addEventListener('keydown', handleKeydown);

  window.DrbConsentManager = {
    open: openModal,
    getState: readSavedState,
    hasConsent: function (category) {
      var saved = readSavedState();
      return saved && saved.consent ? saved.consent[category] === true : false;
    },
    loadConsentedScripts: function () {
      var saved = readSavedState();
      if (saved) loadConsentedScripts(saved.consent);
    },
  };

  loadCustomerPrivacy().then(function (api) {
    customerPrivacy = api;
    initFromShopifyOrSavedState();
    document.dispatchEvent(
      new CustomEvent('drb:consent:ready', {
        detail: {
          state: readSavedState(),
          customerPrivacyAvailable: customerPrivacy !== null,
        },
      })
    );
  });
})();
